import React, { useEffect, useMemo, useState } from 'react';
import {
  Bot,
  Calendar,
  ClipboardList,
  DollarSign,
  FileText,
  Lock,
  Loader2,
  Sparkles,
  UserSearch,
  X,
} from 'lucide-react';
import ModalShell from '../../shared/ModalShell';
import Button from '../../shared/Button';
import Input from '../../shared/Input';
import CustomSelect from '../../shared/CustomSelect';
import {
  useRunWorkspaceAgent,
  useVendorWorkspaceAgentRuns,
  useVendorWorkspaceAgents,
  useVendorWorkspaceLinkOptions,
} from '../../../services/vendorWorkspaceService';
import { useToastContext } from '../../../contexts/ToastContext';
import { Link, useNavigate } from 'react-router-dom';

const AGENT_META = {
  meeting_prep: {
    Icon: Calendar,
    empty: 'No upcoming meetings — runs a generic agenda, or link one in All agents.',
    requiresTarget: false,
  },
  payment_chaser: {
    Icon: DollarSign,
    empty: 'No open invoices — send an invoice first, or pick one in All agents.',
    requiresTarget: true,
  },
  event_pack: {
    Icon: ClipboardList,
    empty: 'No bookings in the next 14 days — link one in All agents.',
    requiresTarget: true,
  },
  lead_qualifier: {
    Icon: UserSearch,
    empty: 'No open leads — link a pending Internal/External lead in All agents.',
    requiresTarget: true,
  },
  invoice_from_notes: {
    Icon: FileText,
    empty: 'Paste notes below (client, items, amounts), then run.',
    requiresTarget: false,
    requiresNotes: true,
  },
};

const agentAcceptsEntity = (agent, entityType) => {
  if (!entityType) return true;
  const types = agent?.entity_types || [];
  if (!types.length) return true;
  return types.includes(entityType);
};

/**
 * Pro “Ask agent” — Quick run for Meeting prep, Payment chaser, Event pack, Lead qualifier.
 * Optional `presetEntity` locks the run to a booking/lead from a detail page.
 * Runs allow-listed tools that draft board cards only (never auto-send).
 *
 * @param {{ type: string, id: string|number, label?: string } | null} [presetEntity]
 */
const AskAgentModal = ({ isOpen, onClose, canAgents = false, presetEntity = null }) => {
  const navigate = useNavigate();
  const { success, error: showError } = useToastContext();
  const { data: agentsRes, isLoading } = useVendorWorkspaceAgents({ enabled: isOpen });
  const { data: runsRes } = useVendorWorkspaceAgentRuns(
    { limit: 5 },
    { enabled: isOpen && canAgents }
  );
  const agentsData = agentsRes?.data || {};
  const agents = agentsData.agents || [];
  const suggestions = agentsData.suggestions || {};
  const unlocked = Boolean(agentsData.unlocked ?? canAgents);
  const recentRuns = runsRes?.data || [];

  const hasPreset = Boolean(presetEntity?.type && presetEntity?.id);
  const presetLabel =
    presetEntity?.label ||
    (hasPreset ? `${String(presetEntity.type).replace(/_/g, ' ')} #${presetEntity.id}` : '');

  const [agentKey, setAgentKey] = useState('');
  const [linkRef, setLinkRef] = useState('');
  const [notes, setNotes] = useState('');
  const [mode, setMode] = useState('quick');

  useEffect(() => {
    if (!isOpen) {
      setAgentKey('');
      setLinkRef('');
      setNotes('');
      setMode('quick');
      return;
    }
    if (hasPreset) {
      setLinkRef(`${presetEntity.type}:${presetEntity.id}`);
      setMode('quick');
    }
  }, [isOpen, hasPreset, presetEntity?.type, presetEntity?.id]);

  const selected = useMemo(
    () => agents.find((a) => a.key === agentKey) || null,
    [agents, agentKey]
  );

  const compatibleAgents = useMemo(() => {
    if (!hasPreset) return agents;
    return agents.filter((a) => agentAcceptsEntity(a, presetEntity.type));
  }, [agents, hasPreset, presetEntity?.type]);

  const quickAgents = useMemo(() => {
    const flagged = compatibleAgents.filter((a) => a.v1 || a.auto_pick);
    if (flagged.length) return flagged;
    return compatibleAgents.filter((a) =>
      ['meeting_prep', 'payment_chaser', 'event_pack', 'lead_qualifier', 'invoice_from_notes'].includes(
        a.key
      )
    );
  }, [compatibleAgents]);

  const { data: linkRes } = useVendorWorkspaceLinkOptions({
    enabled: isOpen && unlocked && mode === 'advanced' && !hasPreset,
  });
  const linkOptions = useMemo(() => {
    if (hasPreset) {
      return [
        {
          value: `${presetEntity.type}:${presetEntity.id}`,
          label: presetLabel || 'This record',
        },
      ];
    }
    const raw = linkRes?.data?.options || [];
    const allowed = selected?.entity_types || [];
    const filtered = raw.filter((o) => {
      if (!o.value) return true;
      const [type] = String(o.value).split(':');
      if (!allowed.length) return true;
      return allowed.includes(type);
    });
    return filtered.map((o) => ({ value: o.value || '', label: o.label || 'No link' }));
  }, [linkRes, selected, hasPreset, presetEntity, presetLabel]);

  const runAgent = useRunWorkspaceAgent({
    onSuccess: (res) => {
      const count = res?.data?.result?.created_item_ids?.length || 0;
      const invoiceDraft = (res?.data?.result?.drafts || []).find(
        (d) => d.kind === 'invoice_prefill' && d.builder_path
      );
      success(
        res?.message || 'Agent finished',
        invoiceDraft
          ? 'Opening Custom Invoice Builder with your prefill — confirm before create.'
          : count
            ? `${count} card(s) added to your board — review before sending.`
            : undefined
      );
      setNotes('');
      setLinkRef('');
      onClose?.();
      if (invoiceDraft?.builder_path) {
        navigate(invoiceDraft.builder_path);
      }
    },
    onError: (err) =>
      showError(
        err?.response?.data?.message || 'Agent could not run',
        err?.response?.data?.upgrade_required ? 'Agents require Pro.' : undefined
      ),
  });

  const agentOptions = useMemo(
    () => [
      { value: '', label: 'Choose an agent…' },
      ...compatibleAgents.map((a) => ({ value: a.key, label: a.title })),
    ],
    [compatibleAgents]
  );

  const runWith = (key, entityType = null, entityId = null) => {
    runAgent.mutate({
      agent_key: key,
      entity_type: entityType,
      entity_id: entityId,
      notes: notes.trim() || null,
    });
  };

  const handleAdvancedRun = () => {
    if (!agentKey) {
      showError(null, 'Choose an agent first.');
      return;
    }
    if (
      (selected?.requires_notes || agentKey === 'invoice_from_notes') &&
      !notes.trim()
    ) {
      showError(null, 'Add notes describing the invoice before running.');
      return;
    }
    let entityType = null;
    let entityId = null;
    if (hasPreset) {
      entityType = presetEntity.type;
      entityId = String(presetEntity.id);
    } else if (linkRef) {
      const [type, id] = String(linkRef).split(':');
      entityType = type || null;
      entityId = id || null;
    }
    runWith(agentKey, entityType, entityId);
  };

  return (
    <ModalShell
      isOpen={isOpen}
      onClose={runAgent.isPending ? undefined : onClose}
      labelledBy="ask-agent-title"
      panelClassName="max-w-lg"
    >
      <div className="w-full max-w-lg overflow-hidden rounded-md border border-border bg-surface shadow-sm">
        <div className="flex items-start justify-between gap-3 border-b border-border px-4 py-3 sm:px-5">
          <div>
            <div className="flex items-center gap-2">
              <Bot className="h-4 w-4 text-ink-tertiary" />
              <h2 id="ask-agent-title" className="text-base font-semibold text-ink">
                {hasPreset ? 'Run agent' : 'Ask agent'}
              </h2>
            </div>
            <p className="mt-1 text-xs text-ink-secondary">
              {hasPreset
                ? 'Results land as Workspace cards — you always confirm before send.'
                : 'Agents draft board cards and messages — you always confirm before send.'}
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded-md p-1 text-ink-tertiary hover:bg-surface-muted hover:text-ink"
            aria-label="Close"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="space-y-3 px-4 py-4 sm:px-5">
          {isLoading ? (
            <div className="flex justify-center py-10">
              <Loader2 className="h-6 w-6 animate-spin text-ink" />
            </div>
          ) : !unlocked ? (
            <p className="rounded-md border border-dashed border-border bg-surface-muted/40 px-3 py-2 text-sm text-ink-secondary">
              <Lock className="mr-1 inline h-3.5 w-3.5" />
              Workflow agents are on Pro.{' '}
              <Link
                to="/vendor/subscription"
                className="font-medium text-ink underline-offset-2 hover:underline"
              >
                Upgrade
              </Link>{' '}
              or enable agents on{' '}
              <Link
                to="/vendor/integrations"
                className="font-medium text-ink underline-offset-2 hover:underline"
              >
                Integrations
              </Link>
              .
            </p>
          ) : (
            <>
              {hasPreset ? (
                <p className="rounded-md border border-border bg-surface-muted/40 px-3 py-2 text-xs text-ink-secondary">
                  On:{' '}
                  <span className="font-medium text-ink">{presetLabel}</span>
                </p>
              ) : null}

              <div className="inline-flex rounded-lg border border-border bg-gray-100 p-0.5">
                {[
                  { id: 'quick', label: 'Quick run' },
                  { id: 'advanced', label: 'All agents' },
                ].map((tab) => (
                  <button
                    key={tab.id}
                    type="button"
                    onClick={() => setMode(tab.id)}
                    className={`rounded-md px-3 py-1.5 text-xs font-medium ${
                      mode === tab.id
                        ? 'bg-surface text-ink shadow-sm'
                        : 'text-ink-secondary hover:text-ink'
                    }`}
                  >
                    {tab.label}
                  </button>
                ))}
              </div>

              {mode === 'quick' ? (
                <div className="space-y-3">
                  {quickAgents.length === 0 ? (
                    <p className="text-sm text-ink-secondary">
                      No agents accept this record type. Try All agents or open Workspace.
                    </p>
                  ) : null}
                  {quickAgents.map((agent) => {
                    const meta = AGENT_META[agent.key] || {
                      Icon: Sparkles,
                      empty: 'No suggested target — use All agents to link one.',
                      requiresTarget: false,
                    };
                    const Icon = meta.Icon;
                    const targets = suggestions[agent.key] || [];
                    const top = targets[0];
                    const needsNotes = Boolean(
                      meta.requiresNotes || agent.requires_notes
                    );
                    const disabled =
                      runAgent.isPending ||
                      (meta.requiresTarget && !top && !hasPreset) ||
                      (needsNotes && !notes.trim());

                    return (
                      <div
                        key={agent.key}
                        className="rounded-md border border-border bg-surface-muted/40 p-3 space-y-2"
                      >
                        <div className="flex items-start gap-2">
                          <Icon className="mt-0.5 h-4 w-4 text-ink-tertiary shrink-0" />
                          <div className="min-w-0 flex-1">
                            <p className="text-sm font-semibold text-ink">{agent.title}</p>
                            <p className="text-xs text-ink-secondary">{agent.description}</p>
                            {hasPreset ? (
                              <p className="mt-1 text-[11px] text-ink-tertiary">
                                Target: <span className="text-ink">{presetLabel}</span>
                              </p>
                            ) : top ? (
                              <p className="mt-1 text-[11px] text-ink-tertiary">
                                Suggested: <span className="text-ink">{top.label}</span>
                              </p>
                            ) : (
                              <p className="mt-1 text-[11px] text-ink-tertiary">{meta.empty}</p>
                            )}
                            {needsNotes ? (
                              <p className="mt-1 text-[11px] text-ink-tertiary">
                                Example: Client: Jane · email@x.com · Booth package - $850
                              </p>
                            ) : null}
                          </div>
                        </div>
                        <Button
                          size="sm"
                          className="rounded-md w-full"
                          icon={Bot}
                          loading={runAgent.isPending && agentKey === agent.key}
                          disabled={disabled}
                          onClick={() => {
                            setAgentKey(agent.key);
                            if (hasPreset) {
                              runWith(
                                agent.key,
                                presetEntity.type,
                                String(presetEntity.id)
                              );
                            } else if (top) {
                              runWith(agent.key, top.entity_type, top.entity_id);
                            } else {
                              runWith(agent.key, null, null);
                            }
                          }}
                        >
                          {needsNotes
                            ? `Run ${agent.title}`
                            : hasPreset
                              ? 'Run on this'
                              : top
                                ? 'Run on suggested'
                                : `Run ${agent.title}`}
                        </Button>
                      </div>
                    );
                  })}

                  <Input
                    label="Notes for the agent"
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                    placeholder="e.g. Client: Jane · jane@email.com · Booth package - $850 · Travel $75"
                  />
                </div>
              ) : (
                <div className="space-y-3">
                  <CustomSelect
                    label="Agent"
                    options={agentOptions}
                    value={agentKey}
                    onChange={(v) => {
                      setAgentKey(v);
                      if (!hasPreset) setLinkRef('');
                    }}
                  />
                  {selected ? (
                    <p className="text-xs text-ink-secondary">{selected.description}</p>
                  ) : null}
                  <CustomSelect
                    label={
                      hasPreset
                        ? 'Linked record'
                        : 'Link to (optional — Quick run agents auto-pick when empty)'
                    }
                    options={
                      linkOptions.length ? linkOptions : [{ value: '', label: 'No link' }]
                    }
                    value={linkRef}
                    onChange={hasPreset ? () => {} : setLinkRef}
                    placeholder="Booking, lead, or invoice"
                    disabled={hasPreset}
                  />
                  <Input
                    label="Notes for the agent (optional)"
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                    placeholder="e.g. Focus on Saturday evening package"
                  />
                  <p className="text-[11px] text-ink-tertiary">
                    Allowed tools: create board cards · draft messages (never auto-send).
                  </p>
                  <Button
                    className="w-full rounded-md"
                    icon={Bot}
                    loading={runAgent.isPending}
                    disabled={runAgent.isPending || !agentKey}
                    onClick={handleAdvancedRun}
                  >
                    Run agent
                  </Button>
                </div>
              )}

              {recentRuns.length > 0 && (
                <div className="border-t border-border pt-3">
                  <p className="mb-1.5 text-[10px] font-semibold uppercase tracking-wide text-ink-tertiary">
                    Recent runs
                  </p>
                  <ul className="space-y-1">
                    {recentRuns.slice(0, 5).map((run) => (
                      <li
                        key={run.id}
                        className="flex items-center justify-between gap-2 text-[11px] text-ink-secondary"
                      >
                        <span className="truncate">
                          {run.agent_key?.replace(/_/g, ' ')} · {run.status}
                        </span>
                        <span className="shrink-0 text-ink-tertiary">
                          {run.created_at
                            ? new Date(run.created_at).toLocaleDateString()
                            : ''}
                        </span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </ModalShell>
  );
};

export default AskAgentModal;
